import { isLocal } from '@magicschool/utils/nextjs/metadata';
import { deleteCookie } from '@/util/cookies';

export const IMPERSONATING_COOKIE = 'impersonating';

/** Client/server cookie options for the UI-only impersonating flag. */
export function getImpersonatingCookieOptions() {
  return {
    path: '/',
    secure: !isLocal(),
    sameSite: 'lax' as const,
  };
}

/** Clears the impersonating cookie using attributes that match server-set values (Chrome requires an exact match). */
export function clearImpersonatingCookie(): void {
  const { path, secure, sameSite } = getImpersonatingCookieOptions();
  deleteCookie(IMPERSONATING_COOKIE, path, { secure, sameSite });
}

/** Check if an impersonation session is active */
export function isImpersonating(cookieValue?: string | null): boolean {
  return !!cookieValue;
}

/** Get the actor (impersonator) user ID from the cookie. Returns null for legacy 'true' values. */
export function getImpersonatorUserId(cookieValue?: string | null): string | null {
  if (!cookieValue || cookieValue === 'true') return null;
  return cookieValue;
}

export type ServerImpersonationState = {
  isImpersonating: boolean;
  /** Null when not impersonating, or for legacy `impersonating=true` cookies. */
  impersonatorUserId: string | null;
};

/**
 * Reads the impersonation UI cookie for the current server request.
 *
 * Uses a dynamic `next/headers` import so this module stays importable from
 * client components (which only use the sync helpers above). Returns a safe
 * idle state when `cookies()` is unavailable (e.g. unit tests outside a request).
 */
export async function getServerImpersonationState(): Promise<ServerImpersonationState> {
  try {
    const { cookies } = await import('next/headers');
    const cookieStore = await cookies();
    const cookieValue = cookieStore.get(IMPERSONATING_COOKIE)?.value ?? null;
    return {
      isImpersonating: isImpersonating(cookieValue),
      impersonatorUserId: getImpersonatorUserId(cookieValue),
    };
  } catch {
    return { isImpersonating: false, impersonatorUserId: null };
  }
}
